import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActualUrl, getJobDetailsByBudgetCityId, updateWhatsAppEngagementForJobsApp } from '../services/apiService';
import { urlCache } from '../services/urlCache';
import { isMobileDevice, redirectToAppStore, APP_STORE_URLS } from '../utils/deviceDetection';
import { forceRedirectToMobileApp } from '../utils/mobileAppRedirect';

// Emits a global event 'openJobDetails' with detail: { job }
export default function UrlRouter() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    let cancelled = false;
    let safetyTimer = null;

    const handle = async () => {
      try {
        const currentPath = window.location.pathname;
        console.log('[UrlRouter] UrlRouter component is running, current path:', currentPath);

        // Early handling: if URL already has isClient=true with interview params, navigate directly
        try {
          const qp = new URLSearchParams(window.location.search || '');
          const isClient = qp.get('isClient') === 'true';
          const emailId = qp.get('emailId');
          const mobileNumber = qp.get('mobileNumber');
          const interviewId = qp.get('interviewId');
          const pathLooksLikeInterview = currentPath.includes('/InterviewFeedback') || currentPath.includes('/interviewFeedbackScreen');
          const hasInterviewHints = (emailId && mobileNumber) || interviewId || pathLooksLikeInterview;
          if (isClient && hasInterviewHints) {
            console.log('[UrlRouter] Direct interview handling via URL params (isClient=true). Navigating...');
            // If already on the target route, do nothing
            if (currentPath === '/interviewFeedbackScreen') {
              setIsLoading(false);
              return;
            }
            // Preserve the existing query string so the address bar doesn't lose parameters
            navigate({ pathname: '/interviewFeedbackScreen', search: window.location.search }, {
              replace: false,
              state: { emailId, mobileNumber, interviewId, fromClientUrl: true }
            });
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.warn('[UrlRouter] Failed parsing query params for direct interview handling:', e);
        }

        // Don't call getActualUrl API for root path or known in-app routes
        const inAppPrefixes = [
          '/',
          '/home',
          '/applications',
          '/referrals',
          '/earnings',
          '/profile',
          '/interviews', // interviews has own page; deep links will navigate explicitly below
          '/interviewFeedbackScreen',
        ];
        const isInAppRoute = inAppPrefixes.some(prefix => currentPath === prefix || currentPath.startsWith(prefix + '/'));

        if (isInAppRoute) {
          console.log('[UrlRouter] Skipping getActualUrl API call for in-app route');
          setIsLoading(false);
          return;
        }

        console.log('[UrlRouter] Starting getActualUrl API call...');

        // Show loading spinner while checking which screen to navigate to
        setIsLoading(true);
        // Safety timeout to auto-dismiss loader if API hangs (8s)
        safetyTimer = setTimeout(() => {
          if (!cancelled) {
            console.warn('[UrlRouter] Safety timeout reached. Hiding loading overlay.');
            setIsLoading(false);
          }
        }, 8000);

        // Call getActualUrl to handle dynamic responses like InterviewFeedback
        console.log('[UrlRouter] Making getActualUrl API call...');
        
        // Use cached getActualUrl to prevent duplicate API calls
        const res = await urlCache.getActualUrl(() => getActualUrl());
        if (!res?.success || !res?.data) {
          console.log('[UrlRouter] getActualUrl returned empty or failed:', res);
          setIsLoading(false);
          return;
        }
        const urlStr = (res.data || '').trim();
        console.log('[UrlRouter] getActualUrl resolved to:', urlStr);
        if (!urlStr) {
          setIsLoading(false);
          return;
        }

        const normalizedUrl = urlStr.startsWith('http')
          ? urlStr
          : `${window.location.origin}${urlStr.startsWith('/') ? '' : '/'}${urlStr}`;

        let parsed;
        try {
          parsed = new URL(normalizedUrl);
        } catch (e) {
          console.warn('[UrlRouter] Failed to parse URL:', urlStr);
          setIsLoading(false);
          return;
        }

        // Check for different route types
        const isApplyForJob = parsed.pathname.includes('/ApplyForJob');
        const isJobDetailsDeepLink = parsed.pathname.includes('/getJobDetailsByBudgetCityId');
        console.log('[UrlRouter] Route detection:', {
          pathname: parsed.pathname,
          isApplyForJob,
          isJobDetailsDeepLink,
        });
        
        // Handle deep link for job details by budgetCityId
        if (isJobDetailsDeepLink) {
          console.log('[UrlRouter] Detected getJobDetailsByBudgetCityId deep link');
          
          // Check if user is on mobile device
          if (isMobileDevice()) {
            console.log('[UrlRouter] Mobile device detected, redirecting to app store');
            
            // Try immediate redirect
            redirectToAppStore(APP_STORE_URLS.android, APP_STORE_URLS.ios);
            
            // Show fallback message after 2 seconds if redirect didn't work
            setTimeout(() => {
              if (!cancelled) {
                setIsLoading(false);
              }
            }, 2000);
            
            return;
          }
          
          // User is on web, fetch job details and show apply page
          const params = Object.fromEntries(parsed.searchParams.entries());
          const budgetCityId = params.budgetCityId;
          
          if (!budgetCityId) {
            console.warn('[UrlRouter] No budgetCityId found in deep link URL');
            setIsLoading(false);
            return;
          }
          
          console.log('[UrlRouter] Fetching job details for budgetCityId:', budgetCityId);
          
          // Fetch job details in background
          const jobDetailsPromise = getJobDetailsByBudgetCityId(budgetCityId);
          
          // Navigate to home page immediately
          if (window.location.pathname !== '/') {
            try {
              navigate('/', { replace: false });
            } catch (_) {}
          }
          
          // Wait for job details and open modal
          jobDetailsPromise.then(jobDetailsRes => {
            if (!cancelled && jobDetailsRes?.success && jobDetailsRes?.data) {
              const jobData = jobDetailsRes.data;
              console.log('[UrlRouter] Job details fetched successfully:', jobData);
              
              // Transform API response to match expected format
              const transformedJobData = {
                id: jobData.id || jobData.budgetCityId || Math.random().toString(36).slice(2),
                jobId: jobData.id || jobData.budgetCityId,
                jobName: jobData.budgetName || jobData.title || 'Job Details',
                title: jobData.budgetName || jobData.title || 'Job Details',
                cityName: jobData.cityName || jobData.city || '',
                pincode: jobData.pinCode || jobData.pincode || '',
                maxSalaryLimit: jobData.maxSalaryLimit || jobData.salary,
                recruiterId: jobData.recruiterId,
                positionId: jobData.budgetCityId || jobData.positionId,
                location: jobData.location || 
                  ((jobData.cityName || jobData.city) && (jobData.pinCode || jobData.pincode)
                    ? `${jobData.cityName || jobData.city} - ${jobData.pinCode || jobData.pincode}`
                    : 'Location Not Specified'),
                salary: jobData.salary 
                  ? `Up to ₹${Number(jobData.salary).toLocaleString()} per month`
                  : 'Salary Not Disclosed',
                _params: params,
                _fromDeepLink: true,
                ...jobData
              };
              
              // Persist for later use
              try {
                sessionStorage.setItem('pendingJobDetails', JSON.stringify(transformedJobData));
              } catch (_) {}
              
              // Dispatch event to open job details modal
              setTimeout(() => {
                console.log('[UrlRouter] Dispatching openJobDetails event for deep link');
                window.dispatchEvent(
                  new CustomEvent('openJobDetails', { detail: { job: transformedJobData } })
                );
              }, 150);
            } else {
              console.error('[UrlRouter] Failed to fetch job details:', jobDetailsRes?.error);
            }
          }).catch(err => {
            console.error('[UrlRouter] Error fetching job details:', err);
          });
          
          setIsLoading(false);
          return;
        }
        
        if (!isApplyForJob) {
          console.log('[UrlRouter] Resolved URL is not a handled route. Ignoring.');
          setIsLoading(false);
          return;
        }

        // Params from backend-resolved URL
        const params = Object.fromEntries(parsed.searchParams.entries());
        const sourceParam = (params.source || params.Source || '').trim().toLowerCase();
        const candidateIdFromParams = params.candidateId || params.candidateID || params.candidateid;
        // Fallback: also read recruiterId from current window URL in case backend omitted it
        try {
          const winParams = new URLSearchParams(window.location.search);
          if (!params.recruiterId && winParams.get('recruiterId')) {
            params.recruiterId = winParams.get('recruiterId');
          }
        } catch (_) {}
        // Removed interview handling via getActualUrl. Interviews are handled directly via URL isClient=true.

        // Handle Apply For Job routes (existing logic)
        if (isApplyForJob) {
          // Check if redirectInApp=true parameter exists
          const redirectInApp = params.redirectInApp === 'true';

          const isWhatsAppNotification = sourceParam === 'whatsapp_notification';
          const shouldUpdateWhatsAppEngagement = isWhatsAppNotification && redirectInApp;

          if (shouldUpdateWhatsAppEngagement && candidateIdFromParams) {
            console.log('[UrlRouter] WhatsApp notification source detected with redirectInApp=true - updating engagement flag', {
              candidateId: candidateIdFromParams
            });
            updateWhatsAppEngagementForJobsApp(candidateIdFromParams)
              .then((res) => {
                if (!res?.success) {
                  console.warn('[UrlRouter] WhatsApp engagement update failed', res);
                }
              })
              .catch((err) => {
                console.warn('[UrlRouter] WhatsApp engagement update error', err);
              });
          } else if (shouldUpdateWhatsAppEngagement) {
            console.warn('[UrlRouter] WhatsApp notification source detected with redirectInApp=true but candidateId missing');
          }
          
          if (redirectInApp) {
            console.log('[UrlRouter] redirectInApp=true detected in ApplyForJob URL');
            
            if (isMobileDevice()) {
              console.log('[UrlRouter] Mobile device detected with redirectInApp=true - Redirecting to app');
              // Hide loader immediately so spinner doesn't linger while browser switches to the app
              setIsLoading(false);

              // Pass the actual URL from getActualUrl response to the mobile app
              forceRedirectToMobileApp(urlStr);
              
              return;
            } else {
              console.log('[UrlRouter] Desktop device with redirectInApp=true - Continuing to web app');
              // Desktop users continue with normal flow
            }
          }
          
          const jobId = params.jobId || params.positionId || params.id || params.budgetId || params.budgetCityId;

          // Skip calling getJobById API. We'll rely on the URL params to build minimal data.
          let jobData = null;

          if (!jobData) {
            const maxSalary = params.salary ? Number(params.salary) : undefined;
            jobData = {
              id: jobId || Math.random().toString(36).slice(2),
              jobId: jobId,
              jobName: params.budgetName || params.title || 'Job Details',
              title: params.budgetName || params.title || 'Job Details',
              cityName: params.cityName || params.city || '',
              pincode: params.pinCode || params.pincode || '',
              maxSalaryLimit: Number.isFinite(maxSalary) ? maxSalary : undefined,
              recruiterId: params.recruiterId || undefined,
              // If opened via getActualUrl, treat budgetCityId as positionId
              positionId: params.budgetCityId || params.positionId || undefined,
              location:
                (params.cityName || params.city) && (params.pinCode || params.pincode)
                  ? `${params.cityName || params.city} - ${params.pinCode || params.pincode}`
                  : 'Location Not Specified',
              salary: Number.isFinite(maxSalary)
                ? `Up to ₹${maxSalary.toLocaleString()} per month`
                : 'Salary Not Disclosed',
              _params: params,
              _fromActualUrl: true,
            };
          }

          // Do not prefetch budget details here. Keep params (including budgetId) for the modal to fetch securely.

          // Persist so listeners that mount later can still read it
          try {
            sessionStorage.setItem('pendingJobDetails', JSON.stringify(jobData));
          } catch (_) {}

          // Ensure Home is mounted: if not on '/', navigate to '/'.
          if (window.location.pathname !== '/') {
            try {
              navigate('/', { replace: false });
            } catch (_) {}
          }

          if (!cancelled) {
            // Ensure Home is fully mounted before opening modal
            setTimeout(() => {
              console.log('[UrlRouter] Dispatching openJobDetails event with job data:', jobData);
              window.dispatchEvent(
                new CustomEvent('openJobDetails', { detail: { job: jobData } })
              );
            }, 150); // Increased delay to ensure Homepage is fully ready
          }
        }

      } catch (err) {
        console.warn('[UrlRouter] Error while handling getActualUrl:', err);
        setIsLoading(false);
      } finally {
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        // Ensure loader is hidden if still mounted
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    handle();
    return () => {
      cancelled = true;
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }
    };
  }, [navigate]);

  // Show loading spinner while checking which screen to navigate to
  if (isLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ 
          marginTop: '20px', 
          color: '#6b7280', 
          fontSize: '16px',
          fontWeight: '500'
        }}>
          Checking which screen to load...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return null;
}
